/**
 * Analytics client — fire-and-forget event ingestion for the web SDK.
 *
 * Two-phase, Sentry-style lifecycle:
 *  1. `initAnalytics({ module, provider? })` — called once at the SDK root.
 *     Resolves the ingestion host + per-launch sessionId + the app context
 *     (no identity yet).
 *  2. `setAnalyticsIdentity({ parentService, referenceType, referenceId })` —
 *     called when the identity arrives via IDME_INIT.
 * Then `emit()` (via trackViewed/trackClicked) is called from anywhere. Events
 * are best-effort (keepalive, credentials omit) — a failure never blocks the flow.
 *
 * Transmission is off until the host turns it on with `setAnalyticsEnabled(true)`,
 * which is the rollout switch the launching service controls — see that function.
 */

import { logger } from "../utils/logger";
import { trackAnalyticsSetupError, type AnalyticsSetupErrorReason } from "../utils/sentryTracking";
import { getEnvironmentFromHostname, getAnalyticsDomain, type Environment } from "../utils/apiConfig";

const EVENTS_PATH = "/v1/events";

/**
 * Resolve the applicationId. A single Docker image serves every environment, so
 * each env's id is baked into `.env.production` and the right one is picked at
 * runtime from the hostname (same approach as the API/SDK URL resolution).
 */
function resolveApplicationId(configApplicationId: string | undefined): string {
  const applicationIdByEnv: Record<string, string | undefined> = {
    production: import.meta.env.VITE_ANALYTICS_APPLICATION_ID_PRODUCTION as string | undefined,
    staging: import.meta.env.VITE_ANALYTICS_APPLICATION_ID_STAGING as string | undefined,
    labs: import.meta.env.VITE_ANALYTICS_APPLICATION_ID_LABS as string | undefined,
    dev: import.meta.env.VITE_ANALYTICS_APPLICATION_ID_DEV as string | undefined,
    development: import.meta.env.VITE_ANALYTICS_APPLICATION_ID_DEVELOPMENT as string | undefined,
  };
  return configApplicationId ?? applicationIdByEnv[getEnvironmentFromHostname()] ?? "";
}


const SDK_APP_NAME = "idme-web-sdk";

export type AnalyticsModule = "ssn_card" | "face_liveness" | "legal_id";

export type AnalyticsProvider = "regula" | "veriff" | "iproov";

/** Transport/session config set once at the SDK root. */
export interface AnalyticsConfig {
  /** The embedded experience emitting these events. */
  module: AnalyticsModule;
  /** Vendor, when known at launch. Otherwise call {@link setAnalyticsProvider}. */
  provider?: AnalyticsProvider;
  /**
   * Locale actually displayed — pass `i18n.language` (set from the iframe URL by
   * LocaleProvider), not the browser's preference, which is only the fallback.
   */
  locale?: string;
  /** Override the resolved ingestion host (mainly for tests). */
  baseUrl?: string;
  /** Override the per-launch session id (mainly for tests). */
  sessionId?: string;
  /** Override the originating application id. */
  applicationId?: string;
}

/** Which service launched the SDK — matches tracking-api's `context.app.parent` enum. */
export type ParentService = "iva" | "ipa";
/** What kind of id `referenceId` is — matches tracking-api's `properties.referenceType` enum. */
export type ReferenceType = "attempt_uuid" | "wallet_id";

/**
 * Single source of truth for which `referenceType` each parent service
 * supplies — add new mappings here, not at call sites. Only `iva` is
 * confirmed with its owning team so far; {@link resolveReferenceType}
 * falls back to `"attempt_uuid"` for anything else (e.g. `ipa`, pending
 * confirmation).
 */
export const PARENT_SERVICE_REFERENCE_TYPE: Partial<Record<ParentService, ReferenceType>> = {
  iva: "attempt_uuid",
};

export function resolveReferenceType(parentService: ParentService): ReferenceType {
  return PARENT_SERVICE_REFERENCE_TYPE[parentService] ?? "attempt_uuid";
}

/** Identity triple, bound once the host supplies it (IDME_INIT). */
export interface AnalyticsIdentity {
  parentService: ParentService;
  referenceType: ReferenceType;
  referenceId: string;
}

interface AnalyticsState {
  module: AnalyticsModule;
  provider?: AnalyticsProvider;
  baseUrl: string;
  sessionId: string;
  applicationId: string;
  identity?: AnalyticsIdentity;
  context: Record<string, unknown>;
  /** Free-form `context.traits` — see {@link setAnalyticsTraits}. */
  traits?: Record<string, unknown>;
  /** Gate on `emit()` — see {@link setAnalyticsEnabled}. Starts off. */
  enabled: boolean;
  /** Whether the first `emit()` call this launch has been health-checked yet. */
  firstSendChecked: boolean;
}

let state: AnalyticsState | null = null;

/** Reset in `resetAnalytics()` so a fresh launch can report again if it's also broken. */
let healthReported = false;

/**
 * One Sentry report per launch, whichever trigger fires first — see
 * `trackAnalyticsSetupError` for what each reason means.
 *
 * Exported because an SDK root that catches `initAnalytics` throwing must report
 * through this same guard rather than calling Sentry directly: a throw leaves the
 * client uninitialized, so the next `emit()` would otherwise add a second, vaguer
 * `never_initialized` for the one root cause.
 */
export function reportAnalyticsSetupFailure(reason: AnalyticsSetupErrorReason, context: Record<string, unknown>): void {
  if (healthReported) return;
  healthReported = true;
  trackAnalyticsSetupError(reason, context);
}

/**
 * Resolve the analytics ingestion host once, from the runtime hostname:
 * analytics-api.id.me (prod) / .idme.cc (staging) / .idme.dev (dev) / .idmelabs.co
 * (labs, always — see {@link getAnalyticsDomain}). Local hosts classify as
 * `development`, so they post to analytics-api.idme.dev.
 *
 * Note: the dev endpoint (analytics-api.idme.dev) accepts events but doesn't
 * persist them anywhere. For local testing where you need the data to actually
 * land, temporarily point this at staging by returning the `idme.cc` host.
 */
function resolveBaseUrl(): string {
  const env = getEnvironmentFromHostname() as Environment;
  return `https://analytics-api.${getAnalyticsDomain(env)}`;
}

/**
 * Envelope `context` (the shared idme-context.json fields), resolved once at init.
 * `locale` can change afterwards — see {@link setAnalyticsLocale}. `userAgentData` is
 * Client Hints' low-entropy data, sent only when the browser exposes it.
 */
function resolveContext(locale: string | undefined): Record<string, unknown> {
  if (typeof navigator === "undefined") return {};
  const context: Record<string, unknown> = {
    userAgent: navigator.userAgent,
    locale: locale || navigator.language,
    timezone: typeof Intl !== "undefined" && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
    page: {
      url: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      referrer: document.referrer,
      title: document.title,
    },
  };
  const userAgentData = (navigator as { userAgentData?: unknown }).userAgentData;
  if (userAgentData) {
    context.userAgentData = userAgentData;
  }
  return context;
}

/** Android's UA freezes the OS token to `10` and the model to `K`, so these are the only
 *  source of the real device (VEX-7881). */
const HIGH_ENTROPY_HINTS = ["model", "platformVersion", "fullVersionList"];

interface NavigatorUAData {
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
  toJSON?: () => Record<string, unknown>;
}

/** Fire-and-forget: initAnalytics stays synchronous, so a launch's first event or two may
 *  go without these. Chromium-only, and gated cross-origin unless IFRAME_ALLOW delegates. */
function mergeHighEntropyHints(): void {
  const uad = (navigator as { userAgentData?: NavigatorUAData }).userAgentData;
  if (!uad?.getHighEntropyValues) return;

  void uad
    .getHighEntropyValues(HIGH_ENTROPY_HINTS)
    .then((values) => {
      // A launch that reset in the meantime must not be resurrected.
      if (!state) return;
      // Never spread the live NavigatorUAData: its fields are prototype getters and
      // spread to nothing. toJSON() is the documented way to a plain object.
      const base = typeof uad.toJSON === "function" ? uad.toJSON() : {};
      state.context = { ...state.context, userAgentData: { ...base, ...values } };
    })
    .catch((err) => {
      // Diagnostics must never break a launch: an unrecognised hint name rejects.
      logger.warn("[Analytics] high-entropy Client Hints unavailable:", err);
    });
}

/**
 * Phase 1 — transport + session for one launch (identity bound later).
 * Idempotent: a repeat call means the SDK root remounted, not a new launch, so
 * everything is kept — rebuilding would split one launch into two warehouse sessions.
 * identity/provider/locale are owned by their own setters and survive untouched.
 */
export function initAnalytics(config: AnalyticsConfig): void {
  if (state) {
    if (config.module !== state.module) {
      logger.warn(`[Analytics] re-init with a different module (${state.module} -> ${config.module}); ignoring — one flow per page load`);
    }
    return;
  }

  const applicationId = resolveApplicationId(config.applicationId);
  state = {
    module: config.module,
    provider: config.provider,
    baseUrl: config.baseUrl ?? resolveBaseUrl(),
    sessionId: config.sessionId ?? crypto.randomUUID(),
    applicationId,
    identity: undefined,
    context: resolveContext(config.locale),
    // Off until the host opts in — see setAnalyticsEnabled.
    enabled: false,
    firstSendChecked: false,
  };

  // After `state` is assigned — the callback needs it, and bails if the launch reset.
  mergeHighEntropyHints();

  if (!applicationId) {
    // Warn here (it's the earliest point we know), but don't alert Sentry yet:
    // this runs before the host's enable decision arrives, so alerting here
    // would fire on every analytics-disabled launch. emit() raises it instead,
    // once something actually tries to send. Same for an env that legitimately
    // has no applicationId because it never enables analytics.
    logger.warn("[Analytics] no applicationId configured for this environment (VITE_ANALYTICS_APPLICATION_ID_<ENV> unset); events will be dropped");
  }
}

/** Phase 2 — bind the identity triple once the host supplies it (IDME_INIT). */
export function setAnalyticsIdentity(identity: AnalyticsIdentity): void {
  if (!state) {
    logger.warn("[Analytics] setAnalyticsIdentity() called before initAnalytics(); ignoring");
    return;
  }
  state.identity = identity;
}

/**
 * Update the displayed locale — the member can switch language mid-flow.
 *
 * Only `locale` is refreshed: `context.page.url`/`.path` keep the launch-time
 * locale segment, so segment by `context.locale`, never by the path.
 */
export function setAnalyticsLocale(locale: string): void {
  if (!state) {
    logger.warn("[Analytics] setAnalyticsLocale() called before initAnalytics(); ignoring");
    return;
  }
  if (!locale) return;
  state.context = { ...state.context, locale };
}

/**
 * Turn transmission on or off. This is the launching service's rollout switch:
 * the host passes it in at SDK init and it reaches us through IDME_INIT, so it
 * necessarily arrives *after* `initAnalytics`. Hence a setter rather than an
 * `AnalyticsConfig` field — a config field could never be populated in time.
 *
 * Off is the default, so a host that says nothing gets no events. Gates `emit()`
 * only: session, identity, locale and provider are set up either way, so flipping
 * this on mid-launch doesn't start a second warehouse session.
 *
 * (This is not the removed Osano cookie-consent gate — that read a member choice
 * on the parent page; this is an operational rollout flag owned by the host.)
 */
export function setAnalyticsEnabled(enabled: boolean): void {
  if (!state) {
    logger.warn("[Analytics] setAnalyticsEnabled() called before initAnalytics(); ignoring");
    // Only worth alerting when it was being turned ON — a disabled launch that
    // never initialized has nothing to report.
    if (enabled) {
      reportAnalyticsSetupFailure("never_initialized", { enabled });
    }
    return;
  }
  state.enabled = enabled;
}

/**
 * Merge entries into `context.traits`, the schema's free-form dictionary.
 *
 * For dimensions that describe the whole launch and are the same on every
 * event — not per-event facts, which belong in `properties`. Merges rather than
 * replaces, so separate concerns can each contribute without coordinating, and
 * the key is omitted from the payload entirely while empty so a flow that never
 * calls this sends nothing extra.
 *
 * Like {@link setAnalyticsEnabled}, this must run before the launch's first
 * tracked event, or early events ship without the trait and can't be grouped
 * with the rest of the session.
 */
export function setAnalyticsTraits(traits: Record<string, unknown>): void {
  if (!state) {
    logger.warn("[Analytics] setAnalyticsTraits() called before initAnalytics(); ignoring");
    return;
  }
  state.traits = { ...state.traits, ...traits };
}

export function setAnalyticsProvider(provider: AnalyticsProvider): void {
  if (!state) {
    logger.warn("[Analytics] setAnalyticsProvider() called before initAnalytics(); ignoring");
    return;
  }
  state.provider = provider;
}

/** Clear analytics state (tests / teardown). */
export function resetAnalytics(): void {
  state = null;
  healthReported = false;
}

/** The three event names registered in tracking-api (VEX-7577/#47, VEX-7578/#48). */
export const ANALYTICS_EVENT_NAMES = {
  VIEWED: "Viewed",
  CLICKED: "Clicked",
  CLIENT_OUTCOME: "Client Outcome",
} as const;
export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[keyof typeof ANALYTICS_EVENT_NAMES];

/**
 * Emit one event. `properties` carries the schema-specific keys; the session
 * constants (app context + identity triple, when bound) are merged in automatically.
 */
export function emit(event: AnalyticsEventName, properties: Record<string, unknown>): void {
  if (!state) {
    logger.warn("[Analytics] emit() called before initAnalytics(); dropping event");
    reportAnalyticsSetupFailure("never_initialized", { event });
    return;
  }
  // Rollout gate. After the !state check, before everything else: a disabled launch
  // sends nothing and raises no misconfiguration alert. Setup failures still alert
  // (never_initialized above, init_failed at the SDK root) — a throw leaves no state
  // to hold this flag, so they can't be gated.
  if (!state.enabled) {
    return;
  }
  if (!state.applicationId) {
    // Alert on the first send attempt rather than at init, so only launches that
    // actually wanted to emit are reported (guarded once per launch). No point
    // sending an event known to fail schema validation — applicationId is
    // required, format:"uuid".
    reportAnalyticsSetupFailure("missing_application_id", { module: state.module, event });
    return;
  }

  const elementName = String(properties.elementName ?? "");
  const eventId = crypto.randomUUID();
  const identity = state.identity;
  // Whichever event happens to be first for this launch is this session's health
  // canary — checked once, inside the async callbacks below, without awaiting
  // anything or delaying this fire-and-forget call's return.
  const isFirstSend = !state.firstSendChecked;
  state.firstSendChecked = true;
  const payload = {
    event,
    schemaVersion: "v1",
    eventId,
    sessionId: state.sessionId,
    timestamp: new Date().toISOString(),
    applicationId: state.applicationId,
    context: {
      ...state.context,
      app: {
        ...((state.context.app as Record<string, unknown> | undefined) ?? {}),
        name: SDK_APP_NAME,
        module: state.module,
        ...(state.provider ? { provider: state.provider } : {}),
        ...(identity ? { parent: identity.parentService } : {}),
      },
      ...(state.traits && Object.keys(state.traits).length > 0 ? { traits: state.traits } : {}),
    },
    properties: {
      ...properties,
      ...(identity ? { referenceType: identity.referenceType, referenceId: identity.referenceId } : {}),
    },
  };

  logger.log(`[Analytics] sending ${event} "${elementName}" eventId=${eventId}`);

  try {
    void fetch(`${state.baseUrl}${EVENTS_PATH}`, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (res.ok) {
          logger.log(`[Analytics] ${event} "${elementName}" eventId=${eventId} accepted (HTTP ${res.status})`);
        } else {
          // Non-2xx means validation failed (400 problem+json) — the event was
          // dead-lettered. Log the body so the reason is visible.
          let detail = "";
          try {
            detail = await res.text();
          } catch {
            /* ignore body read errors */
          }
          logger.warn(`[Analytics] ${event} "${elementName}" eventId=${eventId} rejected (HTTP ${res.status}): ${detail}`);
          if (isFirstSend) {
            // `detail` is the server's own problem+json body — usually names the exact
            // schema violation (e.g. a missing/misspelled property), which is the
            // actual answer to "why is this alert firing."
            reportAnalyticsSetupFailure("first_send_failed", {
              event, elementName, module: payload.context.app.module, httpStatus: res.status, detail,
            });
          }
        }
      })
      .catch((err) => {
        logger.warn(`[Analytics] ${event} "${elementName}" eventId=${eventId} send failed (network):`, err);
        if (isFirstSend) {
          reportAnalyticsSetupFailure("first_send_failed", {
            event, elementName, module: payload.context.app.module,
            networkErrorMessage: err instanceof Error ? err.message : String(err),
          });
        }
      });
  } catch (err) {
    logger.warn(`[Analytics] event send threw (eventId=${eventId}):`, err);
  }
}
